import React from "react";
import type { ApolloClient } from "@apollo/client";
import classnames from "classnames";
import { get } from "lodash";
import { Outlet } from "react-router-dom";
import { connect, type ConnectedProps } from "react-redux";
import {
  getAllEmployerAccounts,
  getAllEmployerGroups,
  getBillingPortalURL,
  getCompanyInfo,
} from "actions/employerActions";
import { withApollo, type WithApolloClient } from "@apollo/client/react/hoc";
import { getUserProfile } from "actions/userActions";
import { getAllAccounts } from "actions/accountActions";
import { individualSite } from "utils/determineSite";

import {
  IndividualContributionPending,
  IndividualInvestmentProfileUpdate,
  IndividualPortfolioPending,
  IndividualPortfolioUpdate,
  IndividualRequiredUpdatesPending,
  lifecycleStates,
} from "statics/states";
import {
  isIndividualAccountOverview,
  userIsLockedSelector,
} from "store/selectors/user";

import SideBar from "components/SideBar";
import SubNav from "components/navbar/SubNav";
import IconSpinner from "components/IconSpinner";
import type { RootState } from "types/state";

interface OwnProps {
  isEmployerDashboard?: boolean;
  isAdminDashboard?: boolean;
  color?: string;
  isSmallScreen?: boolean;
  routes?: unknown[];
}

const mapStateToProps = (state: RootState, ownProps: OwnProps) => {
  // `state.user.userState` is the `object`-typed slice, so this deep get is
  // `unknown`; it resolves to the user-state string at runtime — cast at the
  // boundary like the other selectors (mirrors components/SideBar.tsx).
  const userState = get(state.user, "userState.state", "") as string;

  const statesToHideSideBar = [
    IndividualPortfolioPending,
    IndividualContributionPending,
    IndividualInvestmentProfileUpdate,
    IndividualPortfolioUpdate,
    IndividualRequiredUpdatesPending,
  ];

  /*
   * As this is a very high level component of our app that renders routing logic
   * we must be very careful what state vals we are mapping here, we should avoid
   * anything that is dynamic/fetched by multiple components (groups, company info, etc) to avoid
   * recursive mounting loops in children components.
   * */
  return {
    ownProps,
    shouldHideSideBar:
      statesToHideSideBar.includes(userState) &&
      lifecycleStates.includes(userState) &&
      individualSite() &&
      !isIndividualAccountOverview(state),
    shouldHideSubNav: statesToHideSideBar.includes(userState),
    userIsLocked: userIsLockedSelector(state),
    isIndividualDashboard:
      lifecycleStates.includes(userState) && individualSite(),
  };
};
const mapDispatchToProps = {
  getCompanyInfo,
  getUserProfile,
  getAllAccounts,
  getAllEmployerGroups,
  getAllEmployerAccounts,
  getBillingPortalURL,
};

const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

type Props = WithApolloClient<OwnProps & PropsFromRedux>;

interface State {
  image: string | null;
  hasImage: boolean;
  initialFetching: boolean;
}

class DashboardLayout extends React.Component<Props, State> {
  static defaultProps = {
    color: "azure",
  };

  mainPanel: HTMLDivElement | null = null;

  constructor(props: Props) {
    super(props);

    this.state = {
      image: null,
      hasImage: false,
      initialFetching: true,
    };
  }

  async componentDidMount() {
    if (this.props.isAdminDashboard || this.props.userIsLocked) {
      this.setState({ initialFetching: false });
      return;
    }
    if (this.props.isEmployerDashboard) {
      await Promise.all([
        this.props.getCompanyInfo(this.props.client as ApolloClient<object>),
        this.props.getBillingPortalURL(
          this.props.client as ApolloClient<object>,
          window.location.origin + "/dashboard"
        ),
        this.props.getAllEmployerGroups(
          this.props.client as ApolloClient<object>
        ),
        this.props.getAllEmployerAccounts(
          this.props.client as ApolloClient<object>
        ),
      ]);
      this.setState({ initialFetching: false });
    } else {
      // hydrate user profile as various sub components rely on user profile data
      await Promise.all([
        this.props.getUserProfile(this.props.client as ApolloClient<object>),
        this.props.getAllAccounts(this.props.client as ApolloClient<object>),
      ]);
      this.setState({ initialFetching: false });
    }
  }

  componentDidUpdate() {
    // scroll-to-top on navigation is handled by the NavigationScrollReset component
  }

  render() {
    if (this.state.initialFetching) {
      return <IconSpinner centered />;
    }

    const outlet = <Outlet />;

    if (
      this.props.isIndividualDashboard &&
      !this.props.isSmallScreen &&
      !this.props.shouldHideSubNav
    ) {
      return (
        <div>
          <SubNav
            {...this.props}
            // routes is typed loosely here (`unknown[]`, from the original
            // `PropTypes.array`); SubNav wants its own SubNavRoute[] shape.
            // @ts-expect-error inert prop-shape mismatch across the spread
            routes={this.props.routes}
          ></SubNav>
          <div
            // block body so the callback returns void (behavior-identical to
            // the original `(ref) => (... = ref)`; React discards the return).
            ref={(ref) => {
              this.mainPanel = ref;
            }}
          >
            {outlet}
          </div>
        </div>
      );
    } else if (this.props.isSmallScreen) {
      return (
        <div>
          <div className="portfolio-sm-screen">
            <SideBar
              {...this.props}
              // routes is typed loosely here (`unknown[]`, from the original
              // `PropTypes.array`); SideBar wants its own SideBarRoute[] shape.
              // @ts-expect-error inert prop-shape mismatch across the spread
              routes={this.props.routes}
              // image is `string | null` in state; SideBar types it
              // `string | undefined`. Inert at runtime — null was always passed.
              // @ts-expect-error inert null-vs-undefined prop mismatch
              image={this.state.image}
              color={this.props.color}
              hasImage={this.state.hasImage}
              isEmployer={this.props.isEmployerDashboard}
            ></SideBar>
            <div
              // block body so the callback returns void (behavior-identical to
              // the original `(ref) => (... = ref)`; React discards the return).
              ref={(ref) => {
                this.mainPanel = ref;
              }}
            >
              {outlet}
            </div>
          </div>
        </div>
      );
    }
    return (
      <div>
        <div className="portfolio-lg-screen">
          {!this.props.shouldHideSideBar && (
            <SideBar
              {...this.props}
              // routes is typed loosely here (`unknown[]`, from the original
              // `PropTypes.array`); SideBar wants its own SideBarRoute[] shape.
              // @ts-expect-error inert prop-shape mismatch across the spread
              routes={this.props.routes}
              // image is `string | null` in state; SideBar types it
              // `string | undefined`. Inert at runtime — null was always passed.
              // @ts-expect-error inert null-vs-undefined prop mismatch
              image={this.state.image}
              color={this.props.color}
              hasImage={this.state.hasImage}
              isEmployer={this.props.isEmployerDashboard}
            />
          )}

          <div
            className={classnames("main-panel", {
              ["no-sidebar"]:
                this.props.shouldHideSideBar &&
                this.props.isIndividualDashboard,
            })}
            // block body so the callback returns void (behavior-identical to
            // the original `(ref) => (... = ref)`; React discards the return).
            ref={(ref) => {
              this.mainPanel = ref;
            }}
          >
            {outlet}
          </div>
        </div>
      </div>
    );
  }
}

export default connector(withApollo<Props>(DashboardLayout));
